import { useEffect, useMemo, useState } from "react";
import AlarmAddOutlinedIcon from "@mui/icons-material/AlarmAddOutlined";
import { NavLink } from "react-router-dom";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import useClocksRemindersSummary from "components/hooks/useClocksRemindersSummary";
import useGetKlydosByIds from "views/Device/hooks/useGetKlydosByIds";
import firebaseService from "firebase_service/firebaseService";
import { ClockReminderSummaryEntry } from "Types";

const flatCardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "surface.card",
  backgroundImage: "none",
  borderRadius: "20px",
} as const;

const CACHE_KEY = "reminder_klydos_summary_cache";
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function cacheStorageKey() {
  return `${CACHE_KEY}_${firebaseService.getUserId()}`;
}

function readCache(): ClockReminderSummaryEntry[] | null {
  try {
    const raw = localStorage.getItem(cacheStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      data: ClockReminderSummaryEntry[];
      timestamp: number;
    };
    if (!parsed?.timestamp || Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data: ClockReminderSummaryEntry[]) {
  try {
    localStorage.setItem(
      cacheStorageKey(),
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    // ignore quota / serialization errors
  }
}

type Aggregate = {
  klydoId: string;
  reminderCount: number;
  clockCount: number;
};

function ReminderKlydosListWidget() {
  const [enabled, setEnabled] = useState(false);
  const [cached, setCached] = useState<ClockReminderSummaryEntry[] | null>(() =>
    readCache(),
  );
  const {
    data: fetched,
    isFetching,
    error,
    refetch,
  } = useClocksRemindersSummary(enabled);

  useEffect(() => {
    if (fetched) {
      writeCache(fetched);
      setCached(fetched);
    }
  }, [fetched]);

  const data = fetched ?? cached;
  const isLoading = isFetching;

  const handleLoad = () => {
    if (enabled) {
      refetch();
    } else {
      setEnabled(true);
    }
  };

  const aggregates: Aggregate[] = useMemo(() => {
    if (!data) return [];
    const reminderCounts = new Map<string, number>();
    const clockSets = new Map<string, Set<string>>();
    for (const entry of data) {
      for (const [klydoId, reminders] of Object.entries(entry.clockReminders)) {
        if (!reminders?.length) continue;
        reminderCounts.set(
          klydoId,
          (reminderCounts.get(klydoId) ?? 0) + reminders.length,
        );
        let clocks = clockSets.get(klydoId);
        if (!clocks) {
          clocks = new Set<string>();
          clockSets.set(klydoId, clocks);
        }
        clocks.add(entry.clockId);
      }
    }
    return Array.from(reminderCounts.entries())
      .map(([klydoId, reminderCount]) => ({
        klydoId,
        reminderCount,
        clockCount: clockSets.get(klydoId)?.size ?? 0,
      }))
      .sort((a, b) => b.reminderCount - a.reminderCount);
  }, [data]);

  const klydoIds = useMemo(
    () => aggregates.map((a) => a.klydoId),
    [aggregates],
  );
  const { klydos, isLoading: klydosLoading } = useGetKlydosByIds(klydoIds);
  const klydoNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const k of klydos) map.set(k.id, k.name);
    return map;
  }, [klydos]);

  return (
    <DSCard sx={flatCardSx}>
      <DSBox sx={{ px: 2, pt: 2 }}>
        <DSBox
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <DSTypography variant="h6">Reminder Klydos</DSTypography>
          <DSStack direction="row" alignItems="center" spacing={1}>
            <DSSecondaryButton
              size="small"
              onClick={handleLoad}
              disabled={isLoading}
            >
              {isLoading ? (
                <DSCircularProgress size={20} />
              ) : data ? (
                "Refresh"
              ) : (
                "Load"
              )}
            </DSSecondaryButton>
            <DSBox
              sx={{
                color: "surface.accentText",
                "& svg": { fontSize: "1.5rem" },
                display: "flex",
              }}
            >
              <AlarmAddOutlinedIcon />
            </DSBox>
          </DSStack>
        </DSBox>
        <DSTypography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Klydos with reminders set on at least one clock
        </DSTypography>
      </DSBox>
      <DSCardContent sx={{ flex: 1, maxHeight: 420, overflowY: "auto" }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: 2 }}>
            <DSCircularProgress size={20} />
          </DSBox>
        ) : error ? (
          <DSTypography variant="body2" sx={{ color: "error.main" }}>
            Failed to load
          </DSTypography>
        ) : !data ? (
          <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
            Click Load to fetch data
          </DSTypography>
        ) : aggregates.length === 0 ? (
          <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
            No klydos have reminders yet
          </DSTypography>
        ) : (
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSBox
              sx={{
                fontSize: 11,
                color: "text.secondary",
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
                fontFamily: "Gilroy-SemiBold",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              <span>{aggregates.length} klydos</span>
              <span>reminders · clocks</span>
            </DSBox>
            {aggregates.map((agg, i) => (
              <DSBox
                key={agg.klydoId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 0.75,
                  borderTop: i === 0 ? "none" : "1px solid",
                  borderColor: "divider",
                  fontSize: 13,
                }}
              >
                <DSBox
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={agg.klydoId}
                >
                  <NavLink
                    to="/admin/klydos"
                    onClick={() =>
                      localStorage.setItem("klydossearch", agg.klydoId)
                    }
                  >
                    {klydoNameById.get(agg.klydoId) ??
                      (klydosLoading ? "…" : agg.klydoId.substring(0, 14))}
                  </NavLink>
                </DSBox>
                <DSBox
                  sx={{
                    color: "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  <strong style={{ color: "inherit" }}>
                    {agg.reminderCount}
                  </strong>{" "}
                  · {agg.clockCount}
                </DSBox>
              </DSBox>
            ))}
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
}

export default ReminderKlydosListWidget;
